'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

interface TocItem {
  id: string
  label: string
}

interface StickyNavTocProps {
  tocItems: TocItem[]
}

export const StickyNavToc = ({ tocItems }: StickyNavTocProps) => {
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const suppressSpyRef = useRef(false)

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    for (const item of tocItems) {
      const target = document.getElementById(item.id)
      if (!target) {
        continue
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !suppressSpyRef.current) {
            setActiveSection(item.id)
          }
        },
        {
          rootMargin: '-80px 0px -60% 0px',
          threshold: 0,
        }
      )
      observer.observe(target)
      observers.push(observer)
    }

    return () => {
      observers.forEach((o) => o.disconnect())
    }
  }, [tocItems])

  const handleClick = useCallback((e: React.MouseEvent, sectionId: string) => {
    e.preventDefault()
    const target = document.getElementById(sectionId)
    if (!target) {
      return
    }
    setActiveSection(sectionId)
    suppressSpyRef.current = true

    const offset = 120
    const top = target.getBoundingClientRect().top + window.scrollY - offset
    window.scrollTo({ top, behavior: 'auto' })

    let settleId = 0
    let hardId = 0
    const reenable = () => {
      suppressSpyRef.current = false
      window.removeEventListener('scrollend', onScrollEnd)
      window.clearTimeout(settleId)
      window.clearTimeout(hardId)
    }
    // scrollend fires once the scroll settles, but the scrollspy's
    // IntersectionObserver delivers its post-scroll burst slightly later — keep
    // the spy suppressed a moment longer so that burst can't override the
    // clicked tab. Works for both instant and (if we revert) smooth scrolling.
    const onScrollEnd = () => {
      window.clearTimeout(settleId)
      settleId = window.setTimeout(reenable, 100)
    }
    window.addEventListener('scrollend', onScrollEnd)
    // Fallback in case scrollend never fires (e.g. already at position).
    hardId = window.setTimeout(reenable, 1000)
  }, [])

  return (
    <nav className="flex items-center gap-5">
      {tocItems.map((item) => (
        <a
          key={item.id}
          href={`#${item.id}`}
          onClick={(e) => handleClick(e, item.id)}
          data-tracking="ignore"
          className={`border-b-[3px] py-[25px] text-[15px] font-light whitespace-nowrap transition-colors ${
            activeSection === item.id ? 'border-lapis' : 'border-transparent hover:border-blue-500'
          }`}
        >
          {item.label}
        </a>
      ))}
    </nav>
  )
}
